import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Heading,
  useToast,
  ButtonGroup,
  VStack,
  HStack,
  Flex,
  Text,
  SimpleGrid,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import { IActivityScheduleItemBase } from '../../../../../../../../../../../../../../../models/activities';
import {
  createActivityScheduleItemsService,
  INewActivityScheduleItem,
} from '../../../../../../../../../../../../../../../services/Activities/CreateActivityScheduleItemsService';
import { updateActivityScheduleItemsService } from '../../../../../../../../../../../../../../../services/Activities/UpdateActivityScheduleItemsService';
import { translateError } from '../../../../../../../../../../../../../../../utils/errors';
import { MaskedInput } from '../../../../../../../../../../../../../../../components/Form/MaskedInput';
import { ConfirmationModal } from '../../../../../../../../../../../../../../../components/ConfirmationModal';
import { NumberInput } from '../../../../../../../../../../../../../../../components/Form/NumberInput';
import {
  AsyncSelect,
  AsyncSelectOption,
} from '../../../../../../../../../../../../../../../components/Form/AsyncSelect';
import { Switch } from '../../../../../../../../../../../../../../../components/Form/Switch';
import { listUsersService } from '../../../../../../../../../../../../../../../services/Users/ListUsersService';
import { useActivity } from '../../../../../../../../../../../../../../../hooks/activity';
import {
  maskMoney,
  unmaskNumber,
} from '../../../../../../../../../../../../../../../utils/formatters/handleMask';
import { IUserBase } from '../../../../../../../../../../../../../../../models/users';
import {
  ReactSelect,
  SelectOption,
} from '../../../../../../../../../../../../../../../components/Form/ReactSelect';
import { features } from '../../../../../../../../../../../../../../../config/features';

export interface IHandleActivityScheduleItem extends IActivityScheduleItemBase {
  user?: IUserBase;
}

interface IHandleActivityScheduleItemModalFormData {
  description?: string;
  isActive: boolean;
  isRequired: boolean;
  name: string;
  price?: number;
  totalAmount: number;
  selectedUser?: SelectOption;
  costCenter?: string;
  productCode?: string;
  productType?: string;
  productNature?: string;
  invoiceType?: 'NF' | 'ND';
}

interface IHandleActivityScheduleItemModalProps {
  scheduleItem?: IHandleActivityScheduleItem;
  scheduleId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (scheduleItem: INewActivityScheduleItem) => void;
}

const nullableString = (): Yup.StringSchema<
  string | null | undefined,
  Record<string, unknown>,
  string | null | undefined
> =>
  Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value));

const handleActivityScheduleItemModalFormSchema = Yup.object().shape({
  description: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  isActive: Yup.boolean().default(true),
  isRequired: Yup.boolean().default(false),
  name: Yup.string().required('Requerido'),
  price: Yup.number()
    .integer('Valores inteiros')
    .min(0, 'Valores positivos')
    .nullable()
    .transform((_, originalValue) =>
      originalValue === '' ? null : unmaskNumber(originalValue),
    ),
  totalAmount: Yup.number().when('isRequired', {
    is: false,
    then: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .nullable()
      .required('Requerido')
      .transform((_, originalValue) =>
        originalValue === '' ? null : unmaskNumber(originalValue),
      ),
    otherwise: Yup.number()
      .nullable()
      .transform(() => null),
  }),
  selectedUser: Yup.object()
    .shape({
      label: Yup.string().nullable(),
      value: Yup.string().uuid().nullable().nullable(),
    })
    .nullable(),
  costCenter: features.productsServicesEnabled
    ? Yup.string().when('price', {
        is: (val?: number) => !!val && val > 0,
        then: nullableString().required('Requerido para nota fiscal'),
        otherwise: nullableString(),
      })
    : nullableString(),
  productCode: features.productsServicesEnabled
    ? Yup.string().when('price', {
        is: (val?: number) => !!val && val > 0,
        then: nullableString().required('Requerido para nota fiscal'),
        otherwise: nullableString(),
      })
    : nullableString(),
  productType: features.productsServicesEnabled
    ? Yup.string().when('price', {
        is: (val?: number) => !!val && val > 0,
        then: nullableString().required('Requerido para nota fiscal'),
        otherwise: nullableString(),
      })
    : nullableString(),
  productNature: features.productsServicesEnabled
    ? Yup.string().when('price', {
        is: (val?: number) => !!val && val > 0,
        then: nullableString().required('Requerido para nota fiscal'),
        otherwise: nullableString(),
      })
    : nullableString(),
  invoiceType: features.productsServicesEnabled
    ? Yup.string().when('price', {
        is: (val?: number) => !!val && val > 0,
        then: Yup.string()
          .oneOf(['NF', 'ND', null], 'Tipo inválido')
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value,
          )
          .required('Requerido'),
        otherwise: Yup.string()
          .oneOf(['NF', 'ND', null], 'Tipo inválido')
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value,
          ),
      })
    : Yup.string()
        .oneOf(['NF', 'ND', null], 'Tipo inválido')
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value,
        ),
});

export const HandleActivityScheduleItemModal = ({
  scheduleItem,
  scheduleId,
  isOpen,
  onClose,
  onSave,
}: IHandleActivityScheduleItemModalProps): JSX.Element => {
  const toast = useToast();

  const { activity } = useActivity();

  const formRef = useRef<HTMLElement & HTMLFormElement>(null);

  const [
    isVerifyConfirmationModalVisible,
    setIsVerifyConfirmationModalVisible,
  ] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const paymentFeatureEnabled = features.productsServicesEnabled;

  const { register, formState, handleSubmit, reset, control, setValue, watch } =
    useForm({
      resolver: yupResolver(handleActivityScheduleItemModalFormSchema),
    });

  const { errors } = formState;

  const isRequiredSelected: boolean = watch('isRequired');

  const invoiceTypeSelectOptions = useMemo(
    () => [
      {
        label: 'Nota fiscal',
        value: 'NF',
      },
      {
        label: 'Nota débito',
        value: 'ND',
      },
    ],
    [],
  );

  useEffect(() => {
    if (isRequiredSelected) {
      setValue('totalAmount', null);
    }
  });

  const handleToggleVerifyConfirmationModal = useCallback(() => {
    setIsVerifyConfirmationModalVisible((prevState) => !prevState);
  }, []);

  useEffect(() => {
    reset({
      description: scheduleItem?.description,
      isActive: scheduleItem?.isActive,
      isRequired: scheduleItem?.isRequired,
      name: scheduleItem?.name,
      price:
        typeof scheduleItem?.price === 'number'
          ? maskMoney(scheduleItem.price)
          : undefined,
      totalAmount: scheduleItem?.totalAmount,
      selectedUser: scheduleItem?.user
        ? {
            label: scheduleItem.user.name,
            value: scheduleItem.userId,
          }
        : undefined,
      costCenter: scheduleItem?.costCenter,
      productCode: scheduleItem?.productCode,
      productType: scheduleItem?.productType,
      productNature: scheduleItem?.productNature,
      invoiceType: scheduleItem?.invoiceType,
    });
  }, [scheduleItem, reset]);

  const handleActivityScheduleItemSubmit: SubmitHandler<IHandleActivityScheduleItemModalFormData> =
    useCallback(
      async ({
        description,
        isActive,
        isRequired,
        name,
        price,
        totalAmount,
        selectedUser,
        costCenter,
        productCode,
        productType,
        productNature,
        invoiceType,
      }) => {
        if (!scheduleItem) {
          try {
            const newActivityScheduleItem =
              await createActivityScheduleItemsService({
                scheduleId,
                description,
                isActive,
                isRequired,
                name,
                price,
                totalAmount,
                userId: selectedUser?.value,
                costCenter: paymentFeatureEnabled ? costCenter : undefined,
                productCode: paymentFeatureEnabled ? productCode : undefined,
                productType: paymentFeatureEnabled ? productType : undefined,
                productNature: paymentFeatureEnabled
                  ? productNature
                  : undefined,
                invoiceType: paymentFeatureEnabled ? invoiceType : undefined,
              });

            reset({
              description: null,
              isActive: undefined,
              isRequired: false,
              name: undefined,
              price: null,
              totalAmount: null,
              selectedUser: null,
              costCenter: null,
              productCode: null,
              productType: null,
              productNature: null,
              invoiceType: null,
            });

            onSave(newActivityScheduleItem);

            toast({
              title: 'Cadastrado com sucesso',
              description: 'Item cadastrado corretamente.',
              status: 'success',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status !== 401) {
              toast({
                title: 'Falha ao cadastrar',
                description:
                  translateError({ message: err.response?.data.message }) ||
                  'Ocorreu um erro ao cadastrar o item, tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            }
          }
        } else {
          try {
            const updatedActivityScheduleItem =
              await updateActivityScheduleItemsService({
                itemId: scheduleItem.id,
                description,
                isActive,
                isRequired,
                isVerified,
                name,
                price,
                totalAmount,
                userId: selectedUser?.value,
                costCenter: paymentFeatureEnabled ? costCenter : undefined,
                productCode: paymentFeatureEnabled ? productCode : undefined,
                productType: paymentFeatureEnabled ? productType : undefined,
                productNature: paymentFeatureEnabled
                  ? productNature
                  : undefined,
                invoiceType: paymentFeatureEnabled ? invoiceType : undefined,
              });

            reset({
              description: null,
              isActive: undefined,
              isRequired: undefined,
              name: undefined,
              price: null,
              totalAmount: null,
              selectedUser: null,
              costCenter: null,
              productCode: null,
              productType: null,
              productNature: null,
              invoiceType: null,
            });

            onSave(updatedActivityScheduleItem);

            toast({
              title: 'Atualizado com sucesso',
              description: 'Item atualizado corretamente.',
              status: 'success',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status !== 401) {
              if (err.response?.data.message === 'has-bookings-pending') {
                handleToggleVerifyConfirmationModal();

                return;
              }

              toast({
                title: 'Falha ao atualizar',
                description:
                  translateError({ message: err.response?.data.message }) ||
                  'Ocorreu um erro ao atualizar o item, tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            }
          }
        }
      },
      [
        scheduleItem,
        scheduleId,
        reset,
        onSave,
        toast,
        isVerified,
        paymentFeatureEnabled,
        handleToggleVerifyConfirmationModal,
      ],
    );

  const handleVerify = (): void => {
    setIsVerified(true);
    handleToggleVerifyConfirmationModal();
  };

  useEffect(() => {
    if (isVerified) {
      formRef.current?.requestSubmit();
    }
  }, [isVerified]);

  const handleLoadUserSelectOption = useCallback(
    async (name?: string): Promise<AsyncSelectOption[]> => {
      if (!activity) {
        return [];
      }

      const { items: users } = await listUsersService({
        name,
        featureGroups: ['SELLER'],
        ventureId: activity.spot.ventureId,
        limit: 10,
      });

      const parsedUsersSelectOption: AsyncSelectOption[] = [
        ...users.map((user) => ({
          label: user.name,
          value: user.id,
        })),
      ];

      return parsedUsersSelectOption;
    },
    [activity],
  );

  const handleCloseModal = (): void => {
    reset({
      description: null,
      isActive: undefined,
      isRequired: undefined,
      name: undefined,
      price: null,
      totalAmount: null,
      selectedUser: null,
      costCenter: null,
      productCode: null,
      productType: null,
      productNature: null,
      invoiceType: null,
    });

    onClose();
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isVerifyConfirmationModalVisible}
        onClose={handleToggleVerifyConfirmationModal}
        onConfirm={handleVerify}
        message="Existem reservas pendentes, confirmar alterações?"
      />

      <Modal size="2xl" isOpen={isOpen} onClose={handleCloseModal}>
        <ModalOverlay />

        <ModalContent
          as="form"
          ref={formRef}
          onSubmit={handleSubmit(handleActivityScheduleItemSubmit)}
        >
          <ModalHeader>
            <Heading size="lg" fontWeight="normal">
              Item
            </Heading>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <VStack spacing="8">
              <MaskedInput
                label="Nome"
                type="name"
                error={errors.name}
                {...register('name')}
              />

              <MaskedInput
                label="Descrição"
                error={errors.description}
                {...register('description')}
              />

              <SimpleGrid minChildWidth="120px" spacing="4" w="100%">
                <MaskedInput
                  label="Valor"
                  mask="money"
                  error={errors.price}
                  {...register('price')}
                />

                {!isRequiredSelected && (
                  <NumberInput
                    label="Quantidade disponível"
                    error={errors.totalAmount}
                    {...register('totalAmount')}
                  />
                )}

                {paymentFeatureEnabled && (
                  <ReactSelect
                    label="Tipo de nota"
                    name="invoiceType"
                    options={invoiceTypeSelectOptions}
                    control={control}
                    error={errors.invoiceType}
                  />
                )}
              </SimpleGrid>

              {paymentFeatureEnabled && (
                <SimpleGrid minChildWidth="240px" spacing="4" w="100%">
                  <MaskedInput
                    label="Centro de custo"
                    error={errors.costCenter}
                    {...register('costCenter')}
                  />

                  <MaskedInput
                    label="Código de produto"
                    error={errors.productCode}
                    {...register('productCode')}
                  />

                  <MaskedInput
                    label="Tipo de produto"
                    error={errors.productType}
                    {...register('productType')}
                  />

                  <MaskedInput
                    label="Natureza de produto"
                    error={errors.productType}
                    {...register('productNature')}
                  />
                </SimpleGrid>
              )}

              <AsyncSelect
                label="Prestador de serviço"
                name="selectedUser"
                loadOptions={handleLoadUserSelectOption}
                control={control}
                error={errors.selectedUser}
              />

              <Flex w="full" direction="column" align="center">
                <HStack align="flex-end">
                  <Switch
                    label="Obrigatório"
                    error={errors.isRequired}
                    {...register('isRequired')}
                  />

                  <Switch
                    label="Ativo"
                    error={errors.isActive}
                    defaultChecked={!scheduleItem}
                    {...register('isActive')}
                  />
                </HStack>

                {isRequiredSelected && (
                  <Text fontSize="sm" color="gray.500" mt="4">
                    Será adicionado automaticamente em todas reservas
                  </Text>
                )}
              </Flex>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <ButtonGroup>
              <Button colorScheme="blackAlpha" onClick={handleCloseModal}>
                Cancelar
              </Button>

              <Button
                colorScheme="green"
                type="submit"
                isLoading={formState.isSubmitting}
              >
                Salvar
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
